import type { AxiosInstance } from 'axios';

import {
  NO_ACCESS_MESSAGE,
  endViewAsLocally,
  readViewAs,
} from '@/features/auth/view-as';

const NO_REDIRECT_ON_401 = [
  '/auth/export-confirmation/verify',
  '/auth/export-confirmation/resend',
  '/booking/export/csv',
];

const setupAxios = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    config => {
      // A caller that names its token means it: starting a view-as needs the
      // Admin's, ending one needs that view-as token.
      if (config.headers.Authorization) return config;

      // While viewing as another role every request carries the view-as
      // token; the Admin's own stays in `auth` for when it ends.
      const viewAs = readViewAs();
      if (viewAs) {
        config.headers.Authorization = `Bearer ${viewAs.token}`;
        return config;
      }

      const auth = localStorage.getItem('auth');

      if (auth != null) {
        const accessToken = JSON.parse(auth ?? '').state.access_token;

        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // Don't redirect if already on auth pages (login, forgot-password,
        // reset-password, verify-identity, set-password — a wrong 2FA code
        // or an expired set-password token answers 401)
        const isAuthPage =
          window.location.pathname === '/' ||
          window.location.pathname.startsWith('/forgot-password') ||
          window.location.pathname.startsWith('/reset-password') ||
          window.location.pathname.startsWith('/verify-identity') ||
          window.location.pathname.startsWith('/set-password');

        // A logout on an expired session ends the session itself (useAuth);
        // a reload here would cut that short.
        const isLogout = error.config?.url === '/auth/logout';

        // A 401 on a view-as token never logs the Admin out. "No permission"
        // is the simulated role meeting a page it cannot open, which is what
        // the Admin came to see; anything else means the view-as session
        // ended, so the Admin token takes over again.
        const viewAs = readViewAs();
        const sentViewAs =
          viewAs !== undefined &&
          error.config?.headers?.Authorization === `Bearer ${viewAs.token}`;

        if (sentViewAs && !isLogout) {
          if (error.response?.data?.message !== NO_ACCESS_MESSAGE) {
            endViewAsLocally();
            window.location.reload();
          }
          return Promise.reject(error);
        }

        // On the CSV export a 401 is a wrong or expired code, or the booking
        // API's role check, not a dead session (`useExportConfirmation`).
        const isExportCheck = NO_REDIRECT_ON_401.includes(
          error.config?.url ?? ''
        );

        if (!isAuthPage && !isLogout && !isExportCheck) {
          localStorage.removeItem('auth');
          window.location.href = '/';
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default setupAxios;
